import {
  createSlice,
  PayloadAction,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit'
import sortBy from 'lodash/sortBy'

import { createAppAsyncThunk } from 'reduxTyped'
import { QuestionnaireState } from 'apps/questionnaire/pages/PageQuestionnaire/interfaces'
import { createAnswers } from 'apps/questionnaire/pages/PageQuestionnaire/utils/createAnswers'
import { handleError } from 'shared/api/lib/errors/handlers'
import { questionnaireApi } from 'shared/api/requests/questionnaire'
import {
  Answer,
  AnswerItem,
  Question,
  QuestionnaireType,
} from 'shared/api/requests/questionnaire/schemas'

type QuestionnaireRootState = Pick<RootState, 'newQuestionnaire'>

const createQuestionnaireAsyncThunk = createAppAsyncThunk.withTypes<{
  state: QuestionnaireRootState
  dispatch: ThunkDispatch<QuestionnaireRootState, unknown, UnknownAction>
  extra: unknown
}>()

const initialState: QuestionnaireState = {
  type: null,
  activeQuestionId: null,
  prevQuestionIds: [],
  questions: [],
  answers: [],
  subQuestions: [],
  loading: false,
  isSubmitting: false,
}

export const getQuestions = createQuestionnaireAsyncThunk<Question[], QuestionnaireType>(
  'newQuestionnaire/questions',
  async (type, { dispatch }) => {
    try {
      const response = await questionnaireApi.getPageQuestions({
        query: { type },
      })
      const sortedQuestions = sortBy(response.data.questions, ['position'])

      dispatch(setActiveQuestionId(sortedQuestions[0].id))

      return sortedQuestions
    } catch (error) {
      handleError(error, {
        scope: 'questionnaire',
        section: null,
      })
      return []
    }
  },
)

export const sendAnswers = createQuestionnaireAsyncThunk(
  'newQuestionnaire/sendAnswers',
  async ({ onSuccess }: { onSuccess?: (answers: Answer[]) => void } = {}, { getState }) => {
    const state = getState()

    const { answers, type } = state.newQuestionnaire

    if (!type) return

    try {
      await questionnaireApi.savePageAnswers({
        body: {
          answer: answers,
          type,
        },
      })
      onSuccess?.(answers)
      return true
    } catch (error) {
      handleError(error, {
        scope: 'questionnaire',
        section: null,
      })
      return false
    }
  },
)

export interface SetAnswersItem extends AnswerItem {
  next_question_id?: string | null
}

export const setAnswers = createQuestionnaireAsyncThunk(
  'newQuestionnaire/setAnswers',
  async (
    {
      items,
      type,
      next_question_id,
    }: {
      items: SetAnswersItem[]
      type: string
      next_question_id?: string | null
    },
    { dispatch, getState },
  ) => {
    dispatch(addAnswers({ id: type, answers: items }))

    const state = getState()
    const activeQuestionId = getActiveQuestionId(state)
    const prevQuestionIds = getPrevQuestionIds(state)

    if (activeQuestionId) {
      dispatch(setPrevQuestionIds([...prevQuestionIds, activeQuestionId]))
    }
    if (next_question_id) {
      dispatch(setActiveQuestionId(next_question_id))
    }
    if (items[0].next_question_id) {
      dispatch(setActiveQuestionId(items[0].next_question_id))
    }
  },
)

export const goBack = createQuestionnaireAsyncThunk(
  'newQuestionnaire/goBack',
  async (_args, { dispatch, getState }) => {
    const state = getState()
    const activeQuestionId = getActiveQuestionId(state)
    const prevQuestionIds = getPrevQuestionIds(state)

    const newPrevQuestionIds = [...prevQuestionIds]
    const lastPrevQuestionId = newPrevQuestionIds.pop()

    if (lastPrevQuestionId && activeQuestionId) {
      dispatch(clearAnswers(activeQuestionId))
      dispatch(setPrevQuestionIds(newPrevQuestionIds))
      dispatch(setActiveQuestionId(lastPrevQuestionId))
    }
  },
)

const newQuestionnaireSlice = createSlice({
  name: 'newQuestionnaire',
  initialState,
  reducers: {
    setActiveQuestionId: (state, action: PayloadAction<string | null>) => {
      state.activeQuestionId = action.payload
    },
    setPrevQuestionIds: (state, action: PayloadAction<string[]>) => {
      state.prevQuestionIds = action.payload
    },
    addAnswers: (state, action: PayloadAction<{ id: string; answers: AnswerItem[] }>) => {
      state.answers = state.answers.map((answer) => {
        if (answer.question_id === action.payload.id) {
          return {
            ...answer,
            answers: action.payload.answers,
          }
        }
        return answer
      })
    },
    clearAnswers: (state, action: PayloadAction<string>) => {
      state.answers = state.answers.map((answer) => {
        if (answer.question_id === action.payload) {
          return {
            ...answer,
            answers: [],
          }
        }
        return answer
      })
    },
    setInitialState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getQuestions.pending, (state, action) => {
      state.loading = true
      state.type = action.meta.arg
    })
    builder.addCase(getQuestions.fulfilled, (state, action) => {
      state.loading = false
      state.questions = action.payload
      state.answers = createAnswers(action.payload)
    })
    builder.addCase(sendAnswers.pending, (state) => {
      state.isSubmitting = true
    })
    builder.addCase(sendAnswers.fulfilled, (state) => {
      state.isSubmitting = false
    })
  },
})

export const {
  setActiveQuestionId,
  setPrevQuestionIds,
  addAnswers,
  clearAnswers,
  setInitialState,
} = newQuestionnaireSlice.actions

export const getActiveQuestionId = (state: QuestionnaireRootState) =>
  state.newQuestionnaire.activeQuestionId

export const getPrevQuestionIds = (state: QuestionnaireRootState) =>
  state.newQuestionnaire.prevQuestionIds

export const getQuestionnaireLoading = (state: RootState) => state.newQuestionnaire.loading

const getQuestionnaireAnswers = (state: RootState) => state.newQuestionnaire.answers

export const getActiveQuestionAnswers = (state: RootState) => {
  const activeQuestionId = getActiveQuestionId(state)
  const answers = getQuestionnaireAnswers(state)
  return answers.find((answer) => answer.question_id === activeQuestionId)
}

export const getQuestionnaireType = (state: RootState) => state.newQuestionnaire.type

export const getQuestionnaireCommonQuestionById = (state: RootState, type: string | null) =>
  state.newQuestionnaire.questions.find((question) => question.id === type)

export const getQuestionnaireSubmitting = (state: RootState) => state.newQuestionnaire.isSubmitting

export default newQuestionnaireSlice.reducer
